import { Injectable } from '@angular/core';
import { FileMetadata, FileUtility } from '@utilities/file.utility';
import { FileSdk } from '@sdks/file.sdk';
import { BehaviorSubject, bufferCount, concatMap, EMPTY, forkJoin, from, map, mergeMap, Observable, of, reduce, ReplaySubject, Subject, switchMap, takeUntil } from 'rxjs';
import { catchError, take, tap } from 'rxjs/operators';
import { UploadProgress } from '@models/ui/upload-progress.model';
import { FileUploadRequest } from '@models/ui/file-upload-request.model';
import { FileUploadInstructionResource } from '@models/resource/file-upload-instruction.resource';
import { MultiPartFileUploadInstructionResource } from '@models/resource/multi-part-file-upload-instruction.resource';
import { CompletedUpload } from '@models/ui/completed-upload.model';
import { DatMapSdk } from '@sdks/dat-map.sdk';
import { UploadBatchesSdk } from '@sdks/upload-batches.sdk';
import { HttpUrlEncodingCodec } from '@angular/common/http';

@Injectable({
	providedIn: 'root'
})
export class UploadInstructionsEngine {
	public internalUploadProgress: UploadProgress = {
		completedItems: 0,
		totalItems: 0,
		totalBytes: 0,
		completedBytes: 0,
		percent: 0,
		status: 'inactive',
		pendingItemsToAdd: 0,
		uploadId: null
	};

	public internalUploadProgressSource = new BehaviorSubject(this.internalUploadProgress);

	constructor(
		private fileUtility: FileUtility,
		private fileSdk: FileSdk,
		private datMapSdk: DatMapSdk,
		private uploadBatchSdk: UploadBatchesSdk
	) {	}

	private encoder: HttpUrlEncodingCodec = new HttpUrlEncodingCodec();

	public uploadFileList$(request: UploadFileListRequest): Observable<UploadProgress> {
		const presignedBatchCount = 60;
		const s3BatchCount = 6;

		const uploadProgress: UploadProgress = {
			totalItems: request.files.length,
			totalBytes: request.totalBytes,
			completedBytes: 0,
			completedItems: 0,
			percent: 0,
			status: 'uploading'
		};

		const uploadBatchId = request.uploadBatchId;
		const uploadProgress$ = new ReplaySubject<UploadProgress>(1);
		const error$ = new Subject<Error>();
		const s3UploadQueue$ = new Subject<Observable<CompletedUpload>>();

		const completedMultipartUploadsCache: CompletedUpload[] = [];

		let pendingFiles = [...request.files];
		let isRequestingBatch = false;
		let queuedItems = 0;
		let completedItems = 0;

		// 1. Request next batch of presigned instructions in sets of presignedBatchCount files
		const processNextBatch = () => {
			if (pendingFiles.length === 0 || isRequestingBatch) return;

			isRequestingBatch = true;
			const batchFiles = pendingFiles.slice(0, presignedBatchCount);
			pendingFiles = pendingFiles.slice(presignedBatchCount);

			from(batchFiles).pipe(
				mergeMap((file) =>
					this.fileUtility.getTypeAndExtensionInfo$(file).pipe(
						map((fileData) => this.mapFileMetadata({ ...fileData, file })),
						catchError((error) => {
							console.error('File metadata processing failed:', error);
							error$.next(error);
							return EMPTY;
						})
					)
				),
				bufferCount(batchFiles.length),
				concatMap((fileBatch) => {
					const payload = {
						requests: fileBatch,
						uploadBatchId: uploadBatchId
					};
					console.log(`Requesting presigned production upload URLs for batch of ${fileBatch.length} files`);
					return this.fileSdk.requestProductionUploadInstructions(payload).pipe(
						map((presignedResponse) => {
							const items: Observable<CompletedUpload>[] = [];
							presignedResponse.fileInstructions.forEach((instruction) => {
								const request = fileBatch.find((uploadRequest) => {
									if (instruction.fileUploadType === 'multiple') {
										return (instruction as MultiPartFileUploadInstructionResource).partInstructions.some(part =>
											part.uploadUrl.includes(this.encodePath(uploadRequest.filePath))
										);
									} else {
										return instruction.uploadUrl.includes(this.encodePath(uploadRequest.filePath));
									}
								});
								if (!request) {
									throw new Error(`File not found for instruction: ${instruction.fileName}`);
								}
								const request$ = instruction.fileUploadType === 'multiple'
									? this.assembleMultiPartUploadInstructions(
										instruction as MultiPartFileUploadInstructionResource,
										request,
										uploadBatchId
									)
									: this.assembleSinglePartUploadInstructions(
										instruction,
										request,
										uploadBatchId
									);
								items.push(request$);
							});
							return items;
						}),
						catchError((error) => {
							console.error('Failed to get presigned URLs:', error);
							error$.next(error);
							return EMPTY;
						})
					);
				})
			).subscribe({
				next: (uploadRequests) => {
					queuedItems += uploadRequests.length;
					uploadRequests.forEach((request$) => s3UploadQueue$.next(request$));
					isRequestingBatch = false;

					if (uploadProgress.completedItems >= (uploadProgress.totalItems - pendingFiles.length - presignedBatchCount / 2)) {
						processNextBatch();
					}
					if (pendingFiles.length === 0) {
						s3UploadQueue$.complete();
					}
				},
				error: (error) => {
					error$.next(error);
					isRequestingBatch = false;
				}
			});
		};

		// 2. Start the first batch
		processNextBatch();

		// 3. Process the upload queue with controlled concurrency
		const upload$ = s3UploadQueue$.pipe(
			mergeMap(
				(uploadRequest$: Observable<CompletedUpload>) => uploadRequest$.pipe(
					catchError((error) => {
						console.error('Individual upload failed:', error);
						error$.next(error);
						return EMPTY;
					}),
					map((completedUpload) => {
						completedItems++;
						return completedUpload;
					})
				),
				s3BatchCount // concurrency (6)
			),
			takeUntil(error$.pipe(take(1)))
		);

		// 4. Subscribe to the combined upload stream
		upload$.subscribe({
			next: (completedUpload) => {
				if(completedUpload.uploadId?.trim() != '')
					completedMultipartUploadsCache.push(completedUpload);

				uploadProgress.completedItems++;
				uploadProgress.completedBytes += completedUpload.fileSize;
				uploadProgress.percent = (uploadProgress.completedBytes / uploadProgress.totalBytes) * 100;
				uploadProgress$.next(uploadProgress);

				if (!isRequestingBatch && pendingFiles.length > 0
					&& uploadProgress.completedItems >= (uploadProgress.totalItems - pendingFiles.length - presignedBatchCount / 2)) {
					processNextBatch();
				}

				if (completedItems === queuedItems && pendingFiles.length === 0) {
					this.fileSdk.completeProductionUploads(completedMultipartUploadsCache).pipe(
						mergeMap(() => {
							console.log('All multi-part S3 uploads finalized. Now marking batch complete...');
							return this.uploadBatchSdk.CompleteBatch(uploadBatchId);
						})
					).subscribe({
						next: () => {
							uploadProgress.status = 'complete';
							uploadProgress$.next(uploadProgress);
							uploadProgress$.complete();
						},
						error: (err) => {
							console.error('Failed to finalize multi-part or complete batch:', err);
							uploadProgress.status = 'error';
							uploadProgress$.next(uploadProgress);
							uploadProgress$.error(err);
						}
					});
				}
			},
			error: (error) => {
				console.error('Upload process failed:', error);
				uploadProgress.status = 'error';
				uploadProgress$.next(uploadProgress);
				uploadProgress$.error(error);
			}
		});

		return uploadProgress$.asObservable();
	}

	public createUploadInstructions(files: DataTransferItemList | FileList, uploadId: string, isDatUpload: boolean = false): Observable<CreateUploadInstructionsResponse> {
		const createUploadRequest$ = isDatUpload ? this.createDatFileUploadRequest$(files) : this.createFileUploadRequests$(files);
		return createUploadRequest$.pipe(
			tap((requests) => {
				this.internalUploadProgress.pendingItemsToAdd = this.internalUploadProgress.pendingItemsToAdd + requests.length;
				this.internalUploadProgressSource.next({...this.internalUploadProgress});
			}),
			switchMap((requests) => {
				const payload = {requests, uploadBatchId: uploadId};
				const requestUploadInstructions$ = isDatUpload ? this.datMapSdk.requestDatUploadInstructions(payload) : this.fileSdk.requestUploadInstructions(payload);
				return requestUploadInstructions$.pipe(
					map((serverResponse) => {
						return {
							serverResponse,
							requests
						};
					})
				);
			}),
			map((response) => {
				let totalItems = 0;
				let totalBytes = 0;
				const items = [];

				response.serverResponse.fileInstructions.forEach((instruction) => {
					const request = response.requests.find((uploadRequest) => {
						const normalizePath = (path: string) => path.replace(/\\/g, '/');
						return  normalizePath(uploadRequest.filePath).includes(normalizePath(instruction.sourceFilePath ?? instruction.fileName));
					});
					let request$: Observable<CompletedUpload>;
					if (instruction.fileUploadType === 'multiple') {
						request$ = this.assembleMultiPartUploadInstructions(instruction as MultiPartFileUploadInstructionResource, request, uploadId);
					} else {
						request$ = this.assembleSinglePartUploadInstructions(instruction, request, uploadId);
					}

					totalItems++;
					totalBytes = totalBytes + request.fileSize;
					items.push(request$);
				});
				return {
					totalItems,
					totalBytes,
					items
				};
			})
		);
	}

	public computeDownloadPercent(): number {
		if (this.internalUploadProgress.completedBytes === this.internalUploadProgress.totalBytes) {
			return 100;
		}

		const dataPercentage = Math.floor(this.internalUploadProgress.completedBytes / this.internalUploadProgress.totalBytes * 100);
		const filePercentage = Math.floor((this.internalUploadProgress.completedItems + 1) / this.internalUploadProgress.totalItems * 100);
		return dataPercentage > filePercentage ? filePercentage : dataPercentage;
	}

	private createDatFileUploadRequest$(files: DataTransferItemList | FileList): Observable<FileUploadRequest[]> {
		let fileUploadRequestArray$: Observable<FileUploadRequest[]>;
		if (files instanceof DataTransferItemList) {
			fileUploadRequestArray$ = from(this.fileUtility.getAllFileEntries(files)).pipe(
				switchMap(entries => {
					const fileData = entries.map(entry => this.fileUtility.getFileMetadata$(entry));
					return this.mapFileMetadata$(fileData);
				})
			);
		} else {
			fileUploadRequestArray$ = of(files).pipe(switchMap(entries => {
				const fileData = Array.from(entries).map(file => this.fileUtility.getTypeAndExtensionInfo$(file));
				return this.mapFileMetadata$(fileData);
			}));
		}

		return fileUploadRequestArray$;
	}

	private createFileUploadRequests$(files: DataTransferItemList | FileList): Observable<FileUploadRequest[]> {
		return files instanceof DataTransferItemList
			? this.createPackageFromDataTransferItemList(files)
			: this.createPackageFromFileList(files);
	}

	private createPackageFromDataTransferItemList(files: DataTransferItemList): Observable<FileUploadRequest[]> {
		const entries$ = from(this.fileUtility.getAllFileEntries(files));
		return entries$.pipe(switchMap(entries => {
			const fileData = entries.map(entry => this.fileUtility.getFileMetadata$(entry));
			return this.mapFileMetadata$(fileData);
		}));
	}

	private createPackageFromFileList(files: FileList): Observable<FileUploadRequest[]> {
		return of(files).pipe(switchMap(files => {
			const fileData = Array.from(files).map(file => this.fileUtility.getTypeAndExtensionInfo$(file));
			return this.mapFileMetadata$(fileData);
		}));
	}

	private mapFileMetadata$(fileData: Observable<FileMetadata>[]): Observable<FileUploadRequest[]> {
		return forkJoin(fileData).pipe(map((fa: FileMetadata[]) => fa.map(fileMetadata => ({
			file: fileMetadata.file,
			fileName: fileMetadata.file.name,
			contentType: fileMetadata.mime || 'text/plain',
			fileSize: fileMetadata.file.size,
			filePath: fileMetadata.relativePath ?? fileMetadata.file.name
		} as FileUploadRequest))));
	}

	private mapFileMetadata(fileData: FileMetadata): FileUploadRequest {
		return {
			file: fileData.file,
			fileName: fileData.file.name,
			contentType: fileData.mime || 'text/plain',
			fileSize: fileData.file.size,
			filePath: fileData.file.webkitRelativePath || (fileData.relativePath ?? fileData.file.name)
		} as FileUploadRequest;
	}

	private assembleMultiPartUploadInstructions(instruction: MultiPartFileUploadInstructionResource, originalRequest: FileUploadRequest, lexbeUploadBatchId: string): Observable<CompletedUpload> {
		const fileParts = this.fileUtility.createChunks(originalRequest.file, instruction.partSize);
		return from(instruction.partInstructions).pipe(
			mergeMap((partInstruction) => {
				return this.fileSdk.uploadFilePartToS3({
					partInstruction,
					filePart: fileParts[partInstruction.partNumber - 1],
					isLast: fileParts.length === partInstruction.partNumber,
					contentType: originalRequest.contentType
				});
			}, 2),
			reduce((acc, value) => {
				this.internalUploadProgress.completedBytes += value.partSize;
				this.internalUploadProgress.percent = this.computeDownloadPercent();
				this.internalUploadProgressSource.next({...this.internalUploadProgress});
				acc.partETags.push({partNumber: value.partNumber, eTag: value.eTag});
				acc.fileName = value.fileName;
				return acc;
			}, {
				originalFileId: instruction.fileId,
				fileName: originalRequest.fileName,
				fileSize: originalRequest.fileSize,
				documentId: instruction.documentId,
				uploadId: instruction.uploadId,
				lexbeUploadBatchId: lexbeUploadBatchId,
				partETags: []
			} as CompletedUpload)
		);
	}

	private assembleSinglePartUploadInstructions(instruction: FileUploadInstructionResource, originalRequest: FileUploadRequest, lexbeUploadBatchId: string): Observable<CompletedUpload> {
		return this.fileSdk.uploadSingleFileToS3({
			instruction,
			file: originalRequest.file,
			contentType: originalRequest.contentType
		}).pipe(map(value => {
			value.lexbeUploadBatchId = lexbeUploadBatchId;
			return value;
		}));
	}

	private encodePath(path:string): string {
		return path.split('/').map(s => this.encoder.encodeValue(s).replace(/'/g, "%27")).join('/');
	}
}

interface CreateUploadInstructionsResponse {
	totalItems: number;
	totalBytes: number;
	items: any;
}

interface UploadFileListRequest {
	files: File[];
	uploadBatchId: string;
	totalBytes: number;
}
